import React, { useState, useEffect, useMemo, useRef } from 'react';
import './home.css';
import videoSrc from '../assets/E5 AFTERMOVIE-_2.mp4';
import AffiliateCards from '../components/cards';
import Blogsection from '../components/blogsection.js';
import { Player } from '@lottiefiles/react-lottie-player'; // For Lottie animations
import { FaClock, FaBell } from 'react-icons/fa';
import lineAnimation from '../stripsJSON/lineAnimation.json';
import strokeENG from '../stripsJSON/StrokeENG.json';
import strokeAR from '../stripsJSON/StrokeAR.json';
import pics from '../assets/pics.webp';
import { useLanguage } from '../contexts/LanguageContext';
const FESTIVAL_MONTH = 1; // February (0-indexed months)
const FESTIVAL_START_DAY = 5;
const FESTIVAL_END_DAY = 16;

const getFestivalStartTime = (year) =>
  new Date(year, FESTIVAL_MONTH, FESTIVAL_START_DAY, 0, 0, 0).getTime();

const getFestivalEndTime = (year) =>
  new Date(year, FESTIVAL_MONTH, FESTIVAL_END_DAY, 23, 59, 59).getTime();

const getInitialTargetDate = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentStart = getFestivalStartTime(currentYear);
  const currentEnd = getFestivalEndTime(currentYear);

  if (now.getTime() > currentEnd) {
    return getFestivalStartTime(currentYear + 1);
  }

  return currentStart;
};

function Home() {
  const { isArabic } = useLanguage();
  const [targetDate, setTargetDate] = useState(getInitialTargetDate);
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  const [timeLeft, setTimeLeft] = useState({
    days: '00',
    hours: '00',
    minutes: '00',
  });

  // Use appropriate ribbon animation based on language
  const ribbonAnimation = useMemo(() => {
    return isArabic ? strokeENG : strokeENG;
  }, [isArabic]);

  // Arabic translations
  const translations = {
    days: isArabic ? 'يوم' : 'Days',
    hours: isArabic ? 'ساعة' : 'Hours',
    minutes: isArabic ? 'دقيقة' : 'Minutes',
    dateRange: isArabic ? 'فبراير ٥-٦ و ١٢-١٣' : 'FEB 5-6 & 12-13',
    stages: isArabic ? '٣ مسارح' : '3 Stages',
    djs: isArabic ? '١٠٠+ دي جي' : '100+ DJs',
    joinNextEvent: isArabic ? 'احصل على تذاكرك الآن' : 'Get your Tickets Now',
        // joinNextEvent: isArabic ? 'انضم إلى الحدث القادم' : 'Join Next Event',

    followUs: isArabic ? 'تابعنا' : 'Follow Us',
    embraceFreakness: isArabic ? 'عِش طاقتك الفريكية بأسلوبك الخاص' : 'Embrace Your Freakness in Style',
    comingSoon: isArabic ? 'قريبًا' : 'Coming Soon',
    notifyMe: isArabic ? 'أرسلوا لي إشعارًا' : 'Notify Me',
    downloadApp: isArabic ? 'حمّل تطبيقنا' : 'Download Our App',
    stepIntoFreakShow: isArabic ? 'ادخل عالم ال Freak Show' : 'Step into the Freak Show',
    affiliateText: isArabic
      ? 'تابع أقوى السيتات واللحظات الأسطورية عبر منصة ‎Relive‎، وتعرّف على مجتمعنا الإبداعي الجريء، وانضم كأفلييت لنشر التجربة وكسب مكافآت رهيبة.'
      : 'Catch up on iconic sets and unforgettable moments through our Relive platform, dive deep into our wild, creative community, and spread the word as an affiliate to earn epic rewards.'
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const distance = targetDate - now;

      if (distance <= 0) {
        const targetYear = new Date(targetDate).getFullYear();
        const festivalEnd = getFestivalEndTime(targetYear);

        if (now > festivalEnd) {
          setTargetDate(getFestivalStartTime(targetYear + 1));
        } else {
          setTimeLeft((prev) =>
            prev.days === '00' && prev.hours === '00' && prev.minutes === '00'
              ? prev
              : { days: '00', hours: '00', minutes: '00' }
          );
        }
      } else {
        const days = String(Math.floor(distance / (1000 * 60 * 60 * 24))).padStart(2, '0');
        const hours = String(Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))).padStart(2, '0');
        const minutes = String(Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0');

        setTimeLeft({ days, hours, minutes });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  // Lazy load video using Intersection Observer
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Load video when it enters viewport
            if (video.dataset.src) {
              video.src = video.dataset.src;
              video.removeAttribute('data-src');
              video.load();
              // Play video once loaded
              video.play().catch((error) => {
                console.log('Video autoplay prevented:', error);
              });
              setVideoLoaded(true);
            }
            observer.unobserve(video);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading slightly before entering viewport
      }
    );

    observer.observe(video);

    return () => {
      if (video) {
        observer.unobserve(video);
      }
    };
  }, []);

  const renderDigitBoxes = (timeString) => (
    <>
      <div className="digit-box">
        <span className="digit-box-span">{timeString[0]}</span>
      </div>
      <div className="digit-box">
        <span className="digit-box-span">{timeString[1]}</span>
      </div>
    </>
  );

  return (
    <div>


      <section className="hero-section text-center text-light ">
        <video
          ref={videoRef}
          data-src={videoSrc}
          className="hero-video"
          autoPlay
          loop
          muted
          playsInline
          onLoadedData={() => setVideoLoaded(true)}
        />
        <div className="hero-div">
          <div className="countdown d-flex justify-content-center my-3">

            <div className="time-box px-3 py-2 ">
              <h4>{translations.days}</h4>
              <div className="countdown-time d-flex">
                {renderDigitBoxes(timeLeft.days)}
              </div>
            </div>
            <div className="time-box px-3 py-2 ">
              <h4>{translations.hours}</h4>
              <div className="countdown-time d-flex">
                {renderDigitBoxes(timeLeft.hours)}
              </div>
            </div>
            <div className="time-box px-3 py-2 ">
              <h4>{translations.minutes}</h4>
              <div className="countdown-time d-flex">
                {renderDigitBoxes(timeLeft.minutes)}
              </div>
            </div>
          </div>

          <div className="event-text">
            {/* <p> FEB 5-6</p> */}
            <p> {translations.dateRange}</p>
            <p> {translations.stages}</p>
            <p> {translations.djs}</p>
          </div>

          <div className="cta-buttons">

            <a
              href="https://nofomo.com/events/freaks-of-nature-e7-freak-yard"
              target="_blank"
              rel="noopener noreferrer"
              className="b1 home-btn-join-next-event"
              onClick={(e) => {
                // e.preventDefault();
                const position = {
                  'page': 'home',
                  'nav_bar': 'hero-section',
                  'sub_nav_bar': ''
                };
                const getUTM = () => {
                  const stored = sessionStorage.getItem('utm_params');
                  return stored ? JSON.parse(stored) : {};
                };
                const utm = getUTM();

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  'event': 'action_cta_click',
                  'button_text': 'Join Next Event',
                  'class_id': 'b1 home-btn-join-next-event',
                  'position': position,
                  'referrer_source': document.referrer,
                  'utm_source': utm.utm_source || '',
                  'utm_medium': utm.utm_medium || '',
                  'utm_campaign': utm.utm_campaign || '',
                  'utm_term': utm.utm_term || '',
                  'utm_content': utm.utm_content || '',
                  'gclid': utm.gclid || '',
                  'fbclid': utm.fbclid || '',
                  'ttclid': utm.ttclid || ''
                });

                // Dispatch custom event to open shared popup
                // window.dispatchEvent(new CustomEvent('openSignupPopup', { 
                //   detail: { position } 
                // }));
              }}
            >
              {translations.joinNextEvent}

            </a>
            <a
              className="b2 home-btn-follow-us"
              href="https://www.instagram.com/werfreaksofnature/?follow"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                const getUTM = () => {
                  const stored = sessionStorage.getItem('utm_params');
                  return stored ? JSON.parse(stored) : {};
                };
                const utm = getUTM();

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  'event': 'action_social_share_click',
                  'platform': 'instagram',
                  'content_title': translations.followUs,
                  'referrer_source': document.referrer,
                  'utm_source': utm.utm_source || '',
                  'utm_medium': utm.utm_medium || '',
                  'utm_campaign': utm.utm_campaign || '',
                  'utm_term': utm.utm_term || '',
                  'utm_content': utm.utm_content || '',
                  'gclid': utm.gclid || '',
                  'fbclid': utm.fbclid || '',
                  'ttclid': utm.ttclid || ''
                });
              }}
            >
              {translations.followUs}
            </a>


          </div>
        </div>

      </section>

      {/* <div className="orange-strip">
        <img src={require(`../assets/orange.png`)} alt="Freaks of Nature" className="img-fluid" />
      </div> */}
      <div className="marquee-container">
        <div className="marquee">
          <Player
            autoplay
            loop
            src={ribbonAnimation}
            className="img-fluid marquee-line"
          />
        </div>
      </div>
      {/* Merch Section */}
      <section className="merch-section">
        <div className="merch-div">
          <h2>{translations.embraceFreakness}</h2>
          <div className="merch-visual">
            <div className="img-cta" />
            <div className="merch-buttons">
              <button
                type="button"
                className="merch-btn coming-soon-btn home-btn-coming-soon"
                onClick={() => {
                  const getUTM = () => {
                    const stored = sessionStorage.getItem('utm_params');
                    return stored ? JSON.parse(stored) : {};
                  };
                  const utm = getUTM();

                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    'event': 'action_cta_click',
                    'button_text': 'Coming Soon',
                    'class_id': 'merch-btn coming-soon-btn home-btn-coming-soon',
                    'position': {
                      'page': 'home',
                      'nav_bar': 'merch-section',
                      'sub_nav_bar': ''
                    },
                    'referrer_source': document.referrer,
                    'utm_source': utm.utm_source || '',
                    'utm_medium': utm.utm_medium || '',
                    'utm_campaign': utm.utm_campaign || '',
                    'utm_term': utm.utm_term || '',
                    'utm_content': utm.utm_content || '',
                    'gclid': utm.gclid || '',
                    'fbclid': utm.fbclid || '',
                    'ttclid': utm.ttclid || ''
                  });
                }}
              >
                <FaClock className="merch-icon" aria-hidden="true" />
                <span>{translations.comingSoon}</span>
              </button>
              <button
                type="button"
                className="merch-btn notify-btn home-btn-notify-me"
                onClick={() => {
                  const position = {
                    'page': 'home',
                    'nav_bar': 'merch-section',
                    'sub_nav_bar': ''
                  };
                  const getUTM = () => {
                    const stored = sessionStorage.getItem('utm_params');
                    return stored ? JSON.parse(stored) : {};
                  };
                  const utm = getUTM();

                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    'event': 'action_cta_click',
                    'button_text': translations.notifyMe,
                    'class_id': 'merch-btn notify-btn home-btn-notify-me',
                    'position': position,
                    'referrer_source': document.referrer,
                    'utm_source': utm.utm_source || '',
                    'utm_medium': utm.utm_medium || '',
                    'utm_campaign': utm.utm_campaign || '',
                    'utm_term': utm.utm_term || '',
                    'utm_content': utm.utm_content || '',
                    'gclid': utm.gclid || '',
                    'fbclid': utm.fbclid || '',
                    'ttclid': utm.ttclid || ''
                  });

                  // Dispatch custom event to open shared popup
                  window.dispatchEvent(new CustomEvent('openSignupPopup', {
                    detail: { position }
                  }));
                }}
              >
                <FaBell className="merch-icon" aria-hidden="true" />
                <span>{translations.notifyMe}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      {/* <section className="download-section">
        <div className='download-div'> 
          <div className='download-text'>
           <h2>Download Our App</h2>
           <p>Plan your ideal schedule, share top artists with friends, and explore amazing food and entertainment. This app has everything you need!</p>
           <div className="cta-buttons mt-0">
            <button className="app-cta apple" ></button>
            <button className="app-cta play" ></button>
        </div>
          </div>
          <div className='download-img'>
            <img src={image1} alt="Freaks of Nature" className="img-fluid" />  
          </div>
          
        </div>
      </section> */}

      {/* Affiliate Section */}
      <section className="affiliate-section py-5 text-center">
        <div className="affiliate-div">
          <h2>{translations.stepIntoFreakShow}</h2>
          <p className='para'>{translations.affiliateText}</p>

          <AffiliateCards />

        </div>
      </section>

      <div className="marquee-bottom" >
        <div className="bottom-marquee" >
          <Player
            autoplay
            loop
            src={ribbonAnimation}
            className="img-fluid marquee-line"
          />
        </div>
      </div>

      <Blogsection />

    </div>
  );
}

export default Home;
